"use client";

import { Dispatch, SetStateAction, useMemo } from "react";

import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TablePagination,
  TableRow,
} from "@/components/ui/table";
import { RoomSystemSessionsResponseItem } from "@classview/shared-types";
import {
  PaginationState,
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  useReactTable,
} from "@tanstack/react-table";

interface RoomSystemSessionsTableProps {
  data: RoomSystemSessionsResponseItem[];
  pagination: PaginationState;
  onPaginationChange: Dispatch<SetStateAction<PaginationState>>;
  pageCount?: number;
  isLoading?: boolean;
}

export function RoomSystemSessionsTable({
  data,
  pagination,
  onPaginationChange,
  pageCount,
  isLoading,
}: RoomSystemSessionsTableProps) {
  const columnHelper = createColumnHelper<RoomSystemSessionsResponseItem>();

  const columns = useMemo(
    () => [
      columnHelper.accessor("sessionName", {
        cell: (info) => info.row.original.sessionName ?? "-",
        header: () => <span>Session Name</span>,
        size: 999,
      }),
      columnHelper.accessor("invitedCount", {
        cell: (info) => <span>{info.getValue()}</span>,
        header: () => <span>Invited</span>,
        size: 150,
      }),

      columnHelper.accessor("attendedCount", {
        cell: (info) => {
          return <span>{info.getValue()}</span>;
        },
        header: () => <span>Attended</span>,
        size: 150,
      }),
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    initialState: {
      pagination: {
        pageSize: pagination.pageSize,
        pageIndex: pagination.pageIndex,
      },
    },
    state: {
      pagination,
    },
    defaultColumn: {
      size: 999,
    },
    manualPagination: true,
    pageCount: pageCount,
    onPaginationChange: onPaginationChange,
  });

  return (
    <Table className="border-0">
      <TableHeader className="border-0">
        {table.getHeaderGroups().map((headerGroup) => (
          <TableRow className="hover:bg-transparent" key={headerGroup.id}>
            {headerGroup.headers.map((header) => {
              return (
                <TableHead
                  key={header.id}
                  style={{
                    width: header.getSize() === 999 ? "auto" : header.getSize(),
                  }}
                >
                  {header.isPlaceholder
                    ? null
                    : flexRender(
                        header.column.columnDef.header,
                        header.getContext(),
                      )}
                </TableHead>
              );
            })}
          </TableRow>
        ))}
      </TableHeader>
      <TableBody>
        {table.getRowModel().rows?.length ? (
          table.getRowModel().rows.map((row) => (
            <TableRow
              key={row.id}
              data-state={row.getIsSelected() && "selected"}
            >
              {row.getVisibleCells().map((cell) => (
                <TableCell key={cell.id}>
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </TableCell>
              ))}
            </TableRow>
          ))
        ) : (
          <TableRow className="hover:bg-transparent">
            <TableCell colSpan={columns.length} className="h-24 text-center">
              No results.
            </TableCell>
          </TableRow>
        )}
      </TableBody>
      <TableFooter>
        <TablePagination table={table} isLoading={isLoading} />
      </TableFooter>
    </Table>
  );
}
